import React, { useState, useEffect, useRef } from 'react';
import AddressAutocomplete from '../components/Common/AddressAutocomplete';
import { FaFolderPlus, FaEdit, FaTrash, FaExclamationCircle } from 'react-icons/fa';
import './Drawer.css';
import ConfirmTooltip from '../components/Common/ConfirmTooltip';

const assignedToOptions = (process.env.REACT_APP_USER_EMAILS || '').split(',').map(e => e.trim()).filter(Boolean);
const statusOptions = ['Open', 'Pending', 'Review', 'Draft Sent', 'eSigned', 'Payment Pending', 'Payment Received', 'Completed'];
const hstPeriodOptions = ['Monthly', 'Quarterly', 'Annually'];
const paymentStatusOptions = ['Unpaid', 'Paid', 'Partially Paid'];

const FORM_SECTIONS = [
  { number: 1, title: 'Company Info' },
  { number: 2, title: 'Assignment' },
  { number: 3, title: 'Filing & Invoice' },
  { number: 4, title: 'Services' },
  { number: 5, title: 'Notes & Details' },
];

const initialCustomerState = {
  businessName: '',
  contactName: '',
  businessNumber: '',
  address: '',
  email: '',
  yearEnd: '',
  todoList: '',
  details: '',
  mobile: '',
  dateFiled: '',
  corporateIncomeTax: '',
  status: 'Pending',
  invoiceAmount: '',
  invoiceNo: '',
  invoiceStatus: '',
  paymentStatus: 'Unpaid',
  notes: '',
  payrollStatus: '',
  payrollAccount: '',
  payrollDateFiled: '',
  payrollAvailable: 'No',
  payrollDueDate: '',
  hstPeriod: 'Annually',
  hstDueDate: '',
  hstStatus: '',
  hstDateFiled: '',
  hstInvoiceStatus: '',
  hstNotes: '',
  hstAvailable: 'No',
  assignedTo: '',
  dueDate: '',
};

const CorporateCustomerDrawer = ({ isOpen, onClose, customer, onSave, onDelete, saving, isAdmin, isDialerOpen, error }) => {
  const [formData, setFormData] = useState(initialCustomerState);
  const [activeSection, setActiveSection] = useState(1);
  const sectionRefs = useRef({});
  const drawerBodyRef = useRef(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Scroll-spy for the tab index — highlights the section currently in view
  useEffect(() => {
    if (!isOpen) return;
    const root = drawerBodyRef.current;
    if (!root) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(Number(entry.target.dataset.section));
          }
        });
      },
      { root, threshold: 0.1, rootMargin: '0px 0px -75% 0px' }
    );

    Object.values(sectionRefs.current).forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && customer && Object.keys(customer).length > 0) {
      // Editing existing customer, pre-fill form
      setFormData({
        businessName: customer.businessName || '',
        contactName: customer.contactName || '',
        businessNumber: customer.businessNumber || '',
        address: customer.address || '',
        email: customer.email || '',
        yearEnd: customer.yearEnd || '',
        todoList: customer.todoList || '',
        details: customer.details || '',
        mobile: customer.mobile || '',
        dateFiled: customer.dateFiled || '',
        corporateIncomeTax: customer.corporateIncomeTax || '',
        status: customer.status || 'Pending',
        invoiceAmount: customer.invoiceAmount || '',
        invoiceNo: customer.invoiceNo || '',
        invoiceStatus: customer.invoiceStatus || '',
        paymentStatus: customer.paymentStatus || 'Unpaid',
        notes: customer.notes || '',
        payrollStatus: customer.payrollStatus || '',
        payrollAccount: customer.payrollAccount || '',
        payrollDateFiled: customer.payrollDateFiled || '',
        payrollAvailable: customer.payrollAvailable || 'No',
        payrollDueDate: customer.payrollDueDate || '',
        hstPeriod: customer.hstPeriod || 'Annually',
        hstDueDate: customer.hstDueDate || '',
        hstStatus: customer.hstStatus || '',
        hstDateFiled: customer.hstDateFiled || '',
        hstInvoiceStatus: customer.hstInvoiceStatus || '',
        hstNotes: customer.hstNotes || '',
        hstAvailable: customer.hstAvailable || 'No',
        assignedTo: customer.assignedTo || '',
        dueDate: customer.dueDate || '',
      });
    } else {
      // Adding new customer or closing, reset to initial state
      setFormData(initialCustomerState);
    }
  }, [isOpen, customer]);

  const scrollToSection = (number) => {
    sectionRefs.current[number]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked ? 'Yes' : 'No' }));
  };

  const handleAddressChange = (value) => {
    setFormData(prev => ({ ...prev, address: value }));
  };

  const handlePlaceSelected = (place) => {
    setFormData(prev => ({ ...prev, address: place.formatted_address }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const isEditing = customer && Object.keys(customer).length > 0;

  return (
    <>
      <div className={`drawer-backdrop ${isOpen ? 'show' : ''}`}></div>
      <div className={`drawer-container ${isOpen ? 'show' : ''} ${isOpen && isDialerOpen ? 'shifted-for-dialer' : ''}`}>
        <div className="drawer-header">
          <div className="d-flex align-items-center gap-3" style={{ minWidth: 0 }}>
            <div className="rounded-4 d-flex align-items-center justify-content-center bg-primary-subtle text-primary flex-shrink-0" style={{ width: '44px', height: '44px' }}>
              {isEditing ? <FaEdit size={18} /> : <FaFolderPlus size={18} />}
            </div>
            <div style={{ minWidth: 0 }}>
              <h4 className="mb-0 fw-bold text-dark text-truncate">{isEditing ? 'Edit Corporate Record' : 'Add New Corporate Record'}</h4>
              <small className="text-muted text-truncate d-block">{isEditing ? `Updating record for ${customer?.businessName || ''}` : 'Create a new corporate tax client'}</small>
            </div>
          </div>
          <button type="button" className="btn-close ms-2 flex-shrink-0" onClick={onClose} aria-label="Close"></button>
        </div>

        {error && (
          <div className="alert alert-danger mx-3 mt-3 mb-0 border-0 rounded-3 small fw-semibold d-flex align-items-center gap-2 shadow-sm" style={{ backgroundColor: '#fee2e2', color: '#dc2626' }}>
            <FaExclamationCircle size={14} className="flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="ledger-tab-bar">
          {FORM_SECTIONS.map(s => (
            <button
              key={s.number}
              type="button"
              className={`ledger-tab-pill ${activeSection === s.number ? 'active' : ''}`}
              onClick={() => scrollToSection(s.number)}
            >
              <span>{s.number}</span>
              <span>{s.title}</span>
            </button>
          ))}
        </div>

        <div className="d-flex flex-grow-1 overflow-hidden">
          <form id="corporate-customer-form" onSubmit={handleSubmit} className="d-flex flex-column flex-grow-1" style={{ overflow: 'hidden' }}>
            <div ref={drawerBodyRef} className="drawer-body">
              <div className="row g-3">
                <h4 ref={el => sectionRefs.current[1] = el} data-section={1} className="form-section-title"><span className="section-number">1</span> Company Info</h4>
                <div className="col-md-6"><label>Business Name</label><input type="text" name="businessName" value={formData.businessName} onChange={handleChange} className="form-control" required /></div>
                <div className="col-md-6"><label>Contact Name</label><input type="text" name="contactName" value={formData.contactName} onChange={handleChange} className="form-control" /></div>
                <div className="col-md-6"><label>Business Number</label><input type="text" name="businessNumber" value={formData.businessNumber} onChange={handleChange} className="form-control" /></div>
                <div className="col-md-6"><label>Mobile</label><input type="tel" name="mobile" value={formData.mobile} onChange={handleChange} className="form-control" required /></div>
                <div className="col-md-6"><label>Email</label><input type="email" name="email" value={formData.email} onChange={handleChange} className="form-control" /></div>
                <div className="col-md-6"><label>Address</label><AddressAutocomplete className="form-control" value={formData.address} onChange={(e) => handleAddressChange(e.target.value)} onPlaceSelected={handlePlaceSelected} /></div>

                <h4 ref={el => sectionRefs.current[2] = el} data-section={2} className="form-section-title"><span className="section-number">2</span> Status & Assignment</h4>
                <div className="col-md-4"><label>Status</label><select name="status" value={formData.status} onChange={handleChange} className="form-select">{statusOptions.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
                <div className="col-md-4"><label>Assigned To</label><select name="assignedTo" value={formData.assignedTo} onChange={handleChange} className="form-select"><option value="">Unassigned</option>{assignedToOptions.map(e => <option key={e} value={e}>{e}</option>)}</select></div>
                <div className="col-md-4"><label>Due Date</label><input type="date" name="dueDate" value={formData.dueDate} onChange={handleChange} className="form-control" /></div>

                <h4 ref={el => sectionRefs.current[3] = el} data-section={3} className="form-section-title"><span className="section-number">3</span> Filing & Invoice</h4>
                <div className="col-md-4"><label>Year End</label><input type="text" name="yearEnd" value={formData.yearEnd} onChange={handleChange} className="form-control" /></div>
                <div className="col-md-4"><label>Date Filed</label><input type="date" name="dateFiled" value={formData.dateFiled} onChange={handleChange} className="form-control" /></div>
                <div className="col-md-4"><label>Corp. Income Tax</label><input type="text" name="corporateIncomeTax" value={formData.corporateIncomeTax} onChange={handleChange} className="form-control" /></div>
                <div className="col-md-4"><label>Invoice No.</label><input type="text" name="invoiceNo" value={formData.invoiceNo} onChange={handleChange} className="form-control" /></div>
                <div className="col-md-4"><label>Invoice Amount</label><input type="text" name="invoiceAmount" value={formData.invoiceAmount} onChange={handleChange} className="form-control" /></div>
                <div className="col-md-4"><label>Payment Status</label><select name="paymentStatus" value={formData.paymentStatus} onChange={handleChange} className="form-select">{paymentStatusOptions.map(s => <option key={s} value={s}>{s}</option>)}</select></div>

                <h4 ref={el => sectionRefs.current[4] = el} data-section={4} className="form-section-title"><span className="section-number">4</span> Services</h4>
              <div className="col-md-6">
                <div className="form-check form-switch">
                  <input className="form-check-input" type="checkbox" role="switch" id="hstAvailable" name="hstAvailable"
                         checked={formData.hstAvailable === 'Yes'}
                         onChange={handleCheckboxChange} />
                  <label className="form-check-label" htmlFor="hstAvailable">HST Available</label>
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-check form-switch">
                  <input className="form-check-input" type="checkbox" role="switch" id="payrollAvailable"
                         name="payrollAvailable"
                         checked={formData.payrollAvailable === 'Yes'}
                         onChange={handleCheckboxChange} />
                  <label className="form-check-label" htmlFor="payrollAvailable">Payroll Available</label>
                </div>
              </div>

              {formData.hstAvailable === 'Yes' && (
                <>
                  <div className="col-12 sub-section-divider">HST Details</div>
                  <div className="col-md-4"><label>HST Period</label><select name="hstPeriod" value={formData.hstPeriod} onChange={handleChange} className="form-select">{hstPeriodOptions.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
                  <div className="col-md-4"><label>HST Due Date</label><input type="date" name="hstDueDate" value={formData.hstDueDate} onChange={handleChange} className="form-control" /></div>
                  <div className="col-md-4"><label>HST Status</label><select name="hstStatus" value={formData.hstStatus} onChange={handleChange} className="form-select"><option value="">-</option>{statusOptions.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
                  <div className="col-md-4"><label>HST Date Filed</label><input type="date" name="hstDateFiled" value={formData.hstDateFiled} onChange={handleChange} className="form-control" /></div>
                  <div className="col-md-8"><label>HST Notes</label><input type="text" name="hstNotes" value={formData.hstNotes} onChange={handleChange} className="form-control" /></div>
                </>
              )}
  
              {formData.payrollAvailable === 'Yes' && (
                <>
                  <div className="col-12 sub-section-divider">Payroll Details</div>
                  <div className="col-md-4"><label>Payroll Account</label><input type="text" name="payrollAccount" value={formData.payrollAccount} onChange={handleChange} className="form-control" /></div>
                  <div className="col-md-4"><label>Payroll Due Date</label><input type="date" name="payrollDueDate" value={formData.payrollDueDate} onChange={handleChange} className="form-control" /></div>
                  <div className="col-md-4"><label>Payroll Status</label><select name="payrollStatus" value={formData.payrollStatus} onChange={handleChange} className="form-select"><option value="">-</option>{statusOptions.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
                  <div className="col-md-4"><label>Payroll Date Filed</label><input type="date" name="payrollDateFiled" value={formData.payrollDateFiled} onChange={handleChange} className="form-control" /></div>
                </>
              )}

                <h4 ref={el => sectionRefs.current[5] = el} data-section={5} className="form-section-title"><span className="section-number">5</span> Notes & Details</h4>
                <div className="col-12"><label>To-Do List</label><textarea name="todoList" value={formData.todoList} onChange={handleChange} className="form-control" rows="2"></textarea></div>
                <div className="col-12"><label>Details</label><textarea name="details" value={formData.details} onChange={handleChange} className="form-control" rows="2"></textarea></div>
                <div className="col-12"><label>Notes</label><textarea name="notes" value={formData.notes} onChange={handleChange} className="form-control" rows="2"></textarea></div>
              </div>
            </div>
            <div className="drawer-footer">
              {isEditing && isAdmin && (
                <div className="position-relative me-auto">
                  <button type="button" className="btn btn-outline-danger rounded-pill px-4" onClick={handleDelete} disabled={saving}>
                    <FaTrash className="me-2" /> Delete
                  </button>
                  <ConfirmTooltip
                    show={showDeleteConfirm}
                    onConfirm={() => { onDelete(customer); setShowDeleteConfirm(false); }}
                    onCancel={() => setShowDeleteConfirm(false)}>
                    Are you sure you want to permanently delete this record? This action cannot be undone.
                  </ConfirmTooltip>
                </div>
              )}
              <button type="button" className="btn btn-light border rounded-pill px-4" onClick={onClose}>Cancel</button>
              <button type="submit" form="corporate-customer-form" className="btn btn-primary rounded-pill px-4" disabled={saving}>
                {saving ? 'Saving...' : (isEditing ? 'Save Changes' : 'Add Record')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default CorporateCustomerDrawer;